import { useEffect, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { Sky } from "@react-three/drei";
import { Perf } from "r3f-perf";
import * as THREE from "three";

import HubScene from "../scene/HubScene";
import RoomScene from "../scene/RoomScene";
import TuyauScene from "../scene/TuyauScene";
import BuzzerScene from "../scene/Buzzer";
import NuageScene from "../scene/NuageScene";
import FuseeScene from "../scene/FuseeScene";
import SecondaryScene from "../scene/SecondaryScene";
import FullScreenVideoOverlay from "./FullScreenVideoOverlay";

// UI
import MoonUI from "../ui/MoonUI/moonUI";
import IntroUI from "../ui/IntroUI/IntroUI";
import InteractUI from "../ui/InteractUI/InteractUI";
import EndUI from "../ui/EndUI/EndUI";
import TransitionUI from "../ui/TransitionUI/TransitionUI";
import CreditsUI from "../ui/CreditsUI";

// Effects & Tools
import Sun from "../effect/Sun";
import CustomCursor from "../effect/CustomCursor";
import CameraManager from "./CameraManager";
import AudioSubtitlePlayer from "./AudioSubtitlePlayer";
import BackgroundMusic from "./BackgroundMusic";
import FlowDebugger from "../../helpers/FlowDebuger";
import MuteButton from "../../assets/svg/MuteButton";

// Store
import useFlowStore from "../store/flowStore";

// Assets
import { audios, videos } from "../../assets/assets";

// Loader
import LoaderUI from "../ui/LoaderUI";
import { EffectComposer, SMAA } from "@react-three/postprocessing";
import preloadAssets from "../../hooks/preloadAssets";
import useCursorStore from "../store/cursorStore";

export default function SceneManager({ perfVisible }) {
  const canvasRef = useRef();
  const ambientLightRef = useRef();
  const interactUIRef = useRef();
  const bgMusicRef = useRef();

  const [showDebugger, setShowDebugger] = useState(true);

  const {
    playingAudio,
    audioSrc,
    subtitleSrc,
    showMoonUI,
    showEndUI,
    showStartUI,
    showInteractUI,
    showTransitionUI,
    videoOverlayVisible,
    setVideoOverlayVisible,
    nextStep,
    currentStep,
    flowSteps,
    visibleScenes,
    setInteractUIRef,
    setStep
  } = useFlowStore();

  const { setCursorTheme } = useCursorStore.getState();

  const step = flowSteps[currentStep];

  useEffect(() => {
    setInteractUIRef(interactUIRef);
  }, [interactUIRef]);


  useEffect(() => {
    setCursorTheme("dark");
    if (!import.meta.env.PROD) {
      setShowDebugger(false);
      setStep(1);
    } else {
      setStep(0);
    }
  }, []);

  // LOAD PRIMARY SCENE AND ASSETS
  const assetsLoaded = preloadAssets();

  return (
    <>
      {/* Debug */}
      {!showDebugger && (<FlowDebugger />)}

      {/* UI */}
      {showStartUI && (
        <IntroUI onStartMusic={() => bgMusicRef.current?.play()} />
      )}
      {showTransitionUI && <TransitionUI />}
      {showMoonUI && <MoonUI interactUIRef={interactUIRef} />}
      {showEndUI && <EndUI />}
      {showInteractUI && (
        <InteractUI
          ref={interactUIRef}
          label={step.interact?.label}
          order={step.interact?.order}
          totalSteps={step.interact?.totalSteps}
          indication={step.interact?.indication}
        />
      )}
      <CreditsUI />

      {/* Loader affiché tant que le principal n'est pas prêt */}
      {showDebugger && (<LoaderUI triggerHide={assetsLoaded} />)}

      {/* Scene rendering */}
      {videoOverlayVisible && (
        <FullScreenVideoOverlay
          src={videos.video}
          onFinished={() => {
            // Ici, on démonte SEULEMENT après le fadeOut !
            setVideoOverlayVisible(false);
            nextStep();
          }}
        />
      )}

      <Canvas
        ref={canvasRef}
        dpr={[1, 2]}
        gl={{
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1,
          antialias: true,
        }}
        // shadows
        // onCreated={({ gl }) => {
        //   // gl.shadowMap.enabled = true;
        //   // gl.shadowMap.type = THREE.PCFSoftShadowMap;
        // }}
      >
        {/* {perfVisible && <Perf position="bottom-left" />} */}
        {/* <EffectComposer multisampling={0}>
          <SMAA />
        </EffectComposer> */}
        <Sky
          distance={450000}
          sunPosition={[0, 3, 10]}
          inclination={1}
          azimuth={1}
        />

        <Sun
          rotate={false}
          basePosition={[2, 20, 20]}
          radius={50}
          speed={0.5}
          inclination={0.5}
          intensity={1}
        />

        <ambientLight intensity={1} ref={ambientLightRef} />

        {visibleScenes.hub && (
          <HubScene
            interactUIRef={interactUIRef}
            visible={visibleScenes.hub}
            onReady={() => {
              console.log("Hub scene ready !");
            }}
          />
        )}

        {visibleScenes.secondaryScene && (
          <SecondaryScene
            visible={visibleScenes.secondaryScene}
            onReady={() => {
              console.log("Secondary scene ready !");
            }}
          >
            <>
              {visibleScenes.secondaryScene && (
                <RoomScene
                  light={ambientLightRef}
                  interactUIRef={interactUIRef}
                />
              )}
              {visibleScenes.secondaryScene && (
                <TuyauScene
                  interactUIRef={interactUIRef}
                  light={ambientLightRef}
                />
              )}
              {visibleScenes.secondaryScene && (
                <BuzzerScene interactUIRef={interactUIRef} />
              )}
              {visibleScenes.secondaryScene && <NuageScene />}
              {visibleScenes.secondaryScene && <FuseeScene />}
            </>
          </SecondaryScene>
        )}
      </Canvas>

      <CustomCursor />
      <MuteButton />

      <BackgroundMusic
        src={audios.background_music}
        volume={0.015}
        ref={bgMusicRef}
      />

      {playingAudio && (
        <AudioSubtitlePlayer
          key={audioSrc}
          audioSrc={audioSrc}
          subtitleSrc={subtitleSrc}
          autoplay
          showSkip
          onAudioComplete={() => {
            useFlowStore.getState().onAudioComplete?.();
          }}
        />
      )}
    </>
  );
}
